import React, { useState, useEffect, useCallback } from "react";
import { FaChartLine, FaSearch } from "react-icons/fa";
import { API_BASE } from "../../api/config";

const API = API_BASE;

const fmt = (v) => {
  if (v == null) return "–";
  return Number(v).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
};
const fmtDate = (d) => {
  if (!d) return "–";
  const parts = d.split("-");
  if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;
  return d;
};

/* ────────────────────────────────────────────────────────────────────────── */
export default function Tab3Miethistorie() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterObjekt, setFilterObjekt] = useState("");
  const [filterEinheit, setFilterEinheit] = useState("");
  const [filterDokument, setFilterDokument] = useState("");
  const [suche, setSuche] = useState("");

  // Inline column filters
  const [colFilterMvNr, setColFilterMvNr] = useState("");
  const [colFilterMieter, setColFilterMieter] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filterObjekt) p.set("objekt", filterObjekt);
      const res = await fetch(`${API}/mietverwaltung/api/miethistorie?${p}`);
      const d = await res.json();
      if (d.status === "ok") setData(d);
    } catch (e) { console.error("Miethistorie:", e); }
    setLoading(false);
  }, [filterObjekt]);

  useEffect(() => { loadData(); }, [loadData]);

  // Filtern
  const filtered = React.useMemo(() => {
    if (!data?.historie) return [];
    let rows = data.historie;
    if (filterEinheit) rows = rows.filter(r => (r.mietvertrag_nr || "").substring(0, 6) === filterEinheit);
    if (filterDokument) rows = rows.filter(r => r.dokument === filterDokument);
    if (colFilterMvNr) {
      const f = colFilterMvNr.toLowerCase();
      rows = rows.filter(r => (r.mietvertrag_nr || "").toLowerCase().includes(f));
    }
    if (colFilterMieter) {
      const f = colFilterMieter.toLowerCase();
      rows = rows.filter(r => (r.name || "").toLowerCase().includes(f));
    }
    if (suche) {
      const s = suche.toLowerCase();
      rows = rows.filter(r =>
        (r.name || "").toLowerCase().includes(s) ||
        (r.mietvertrag_nr || "").toLowerCase().includes(s)
      );
    }
    return rows;
  }, [data, filterEinheit, filterDokument, suche]);

  // Dokument-Typen aus Daten
  const dokTypes = React.useMemo(() => {
    if (!data?.historie) return [];
    const s = new Set(data.historie.map(r => r.dokument).filter(Boolean));
    return [...s].sort();
  }, [data]);

  // Objekte aus Daten
  const objekte = React.useMemo(() => {
    if (!data?.historie) return [];
    const s = new Set(data.historie.map(r => r.objekt).filter(Boolean));
    return [...s].sort();
  }, [data]);

  // Einheiten aus Daten (abhängig vom Objekt-Filter)
  const einheiten = React.useMemo(() => {
    if (!data?.historie) return [];
    let rows = data.historie;
    if (filterObjekt) rows = rows.filter(r => r.objekt === filterObjekt);
    const s = new Set(rows.map(r => (r.mietvertrag_nr || "").substring(0, 6)).filter(Boolean));
    return [...s].sort();
  }, [data, filterObjekt]);

  const kpis = data?.kpis || {};

  if (loading && !data) {
    return <div className="me-loading"><div className="me-spinner" /><p>Lade Miethistorie…</p></div>;
  }

  return (
    <>
      {/* KPIs */}
      <div className="me-kpis me-kpis-small">
        <div className="me-kpi-card">
          <span className="me-kpi-label">Einträge</span>
          <span className="me-kpi-value blue">{kpis.gesamt ?? 0}</span>
        </div>
        <div className="me-kpi-card">
          <span className="me-kpi-label">Mieterhöhungen</span>
          <span className="me-kpi-value amber">{kpis.erhoehungen ?? 0}</span>
        </div>
        <div className="me-kpi-card">
          <span className="me-kpi-label">Neuverträge</span>
          <span className="me-kpi-value green">{kpis.neuvertraege ?? 0}</span>
        </div>
        <div className="me-kpi-card">
          <span className="me-kpi-label">NK-Anpassungen</span>
          <span className="me-kpi-value">{kpis.nk_anpassungen ?? 0}</span>
        </div>
        <div className="me-kpi-card">
          <span className="me-kpi-label">Letzte Erhöhung</span>
          <span className="me-kpi-value" style={{ fontSize: "1rem" }}>{fmtDate(kpis.letzte_erhoehung)}</span>
        </div>
      </div>

      {/* Tabelle */}
      <div className="me-table-section">
        <div className="me-table-header">
          <h3>
            <FaChartLine style={{ marginRight: 6 }} />
            Miethistorie
            {` (${filtered.length})`}
          </h3>
          <div className="me-filters">
            <select className="me-select me-select-sm" value={filterObjekt}
              onChange={(e) => { setFilterObjekt(e.target.value); setFilterEinheit(""); }}>
              <option value="">Alle Objekte</option>
              {objekte.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            <select className="me-select me-select-sm" value={filterEinheit}
              onChange={(e) => setFilterEinheit(e.target.value)}>
              <option value="">Alle Einheiten</option>
              {einheiten.map((e) => <option key={e} value={e}>{e}</option>)}
            </select>
            <select className="me-select me-select-sm" value={filterDokument}
              onChange={(e) => setFilterDokument(e.target.value)}>
              <option value="">Alle Dokumente</option>
              {dokTypes.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <div className="me-search-wrap">
              <FaSearch className="me-search-icon" />
              <input className="me-filter-input" placeholder="Suche…"
                value={suche} onChange={(e) => setSuche(e.target.value)} />
            </div>
          </div>
        </div>

        <div className="me-table-wrap" style={{ maxHeight: "65vh", overflowY: "auto" }}>
          <table className="me-table">
            <thead>
              <tr>
                <th>MV-Nr</th>
                <th>Einheit</th>
                <th>Mieter</th>
                <th>Objekt</th>
                <th>Dokument</th>
                <th>Datum</th>
                <th>Ab</th>
                <th className="num">NKM</th>
                <th className="num">€/qm</th>
                <th className="num">Überw.</th>
              </tr>
              <tr className="me-filter-row">
                <th><input className="me-col-filter" placeholder="Filter…" value={colFilterMvNr} onChange={e => setColFilterMvNr(e.target.value)} /></th>
                <th></th>
                <th><input className="me-col-filter" placeholder="Filter…" value={colFilterMieter} onChange={e => setColFilterMieter(e.target.value)} /></th>
                <th></th><th></th><th></th><th></th><th></th><th></th><th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={10} style={{ textAlign: "center", padding: "2rem", color: "#9ca3af" }}>Keine Einträge.</td></tr>
              ) : (
                filtered.map((r, i) => (
                  <tr key={i}>
                    <td className="me-mv-id">{r.mietvertrag_nr}</td>
                    <td>{(r.mietvertrag_nr || "").substring(0, 6)}</td>
                    <td>{r.name}</td>
                    <td>{r.objekt}</td>
                    <td>
                      <span className={`me-dok-badge ${r.dokument === "Mieterhöhung" ? "dok-erhoehung" : r.dokument === "Mietvertrag" ? "dok-vertrag" : "dok-sonstig"}`}>
                        {r.dokument || "–"}
                      </span>
                    </td>
                    <td>{fmtDate(r.dokument_datum)}</td>
                    <td>{fmtDate(r.miete_ab)}</td>
                    <td className="num">{fmt(r.nettokaltmiete)}</td>
                    <td className="num">{r.nkm_qm != null ? Number(r.nkm_qm).toLocaleString("de-DE", { minimumFractionDigits: 2 }) : "–"}</td>
                    <td className="num">{fmt(r.ueberweisungsbetrag)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
